import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../AuthContext';
import LogoBlog from "../../assets/logo.png";

const PasswordReset = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false); // New state to track valid email
  const [staffs, setStaffs] = useState([]); // Initialize as empty array for safety
  const [foundUser, setFoundUser] = useState([]); // Initialize as empty array for safety


  const { resetPassword, getData } = useAuth();
  const navigate = useNavigate();

  // Fetch staff data to validate email
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getData('proccess.php', 'staffs');
        
        if (response.success) {
          setStaffs(response.data || []); // Ensure data is an array
        } else {
          toast.error('Failed to fetch staff data');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        toast.error('Error fetching staff data');
      }
    };
    fetchData();
  }, [getData]);

  // Handle email validation
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }

    setIsLoading(true);
    try {
      // Check if email exists in staffs array
      const isValidEmail = staffs.some(staff => staff.email?.toLowerCase() === email.toLowerCase());
      if (isValidEmail) {
        setIsEmailValid(true);
        setFoundUser(staffs.find(user => user.email === email));
        toast.success('Email verified. Please enter new password.');
      } else {
        toast.error('No staff found with this email');
      }
    } catch (err) {
      console.error('Email check error:', err);
      toast.error('Error verifying email');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      const result = await resetPassword('proccess.php', 'staffs', {
        email,
        password,
        confirmPassword
      });

      if (result.success) {
        toast.success(result.message);
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setFoundUser([]);
        setRememberMe(false);
        setIsEmailValid(false); // Reset to email input step
      } else {
        console.error('Reset error:', result);
        toast.error(result.message);
      }
    } catch (err) {
      console.error('Reset error:', err);
      toast.error(err.message || 'Password reset failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const redirect = () => {
    setIsLoading(true);
    navigate(-1);
  };

  const resetForm = () => {
    setIsLoading(true);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFoundUser([]);
    setRememberMe(false);
    setIsEmailValid(false); // Reset to email input step
    setIsLoading(false);
  };

  return (
    <div style={{ height: "100vh", width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div className="login-container">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        
        <div className="login-box bg-white">
          <div className='flex-raw-center w-100 mb-4 pb-4'>
            <div className='small-img'>
              <img className='image' src={LogoBlog} alt='' />
            </div>
          </div>
          
          {!isEmailValid ? (
            // Email input form
            <form onSubmit={handleEmailSubmit} className="login-form" method='post'>
              <div className="form-group">
                <input
                  type="hidden"
                  id="action"
                  value="checkEmail"
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Enter Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={isLoading}
                />
              </div>
              <button
                type="submit"
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span> Verifying...
                  </>
                ) : 'Verify Email'}
              </button>
            </form>
          ) : (
            // Password reset form
            <form onSubmit={handlePasswordSubmit} className="login-form" method='post'>
              <div className="form-group">
                <p style={{color: 'gray'}}>Email: {foundUser.email} ID: {foundUser.Id}</p>
                <p style={{color: 'gray'}}>{foundUser.FirstName} {foundUser.LastName}</p>
              </div>
              <div className="form-group">
                <label htmlFor="password">Enter Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                />
              </div>
              <div className="form-options">
                <div className="remember-me">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                  />
                  <label htmlFor="rememberMe" className='text-dark'>I'm not a robot</label>
                </div>
              </div>
              <button
                type="submit"
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span> Resetting...
                  </>
                ) : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
        <div>
            <button
                onClick={redirect}
                style={{
                padding: '.3rem 1rem',
                border: 'none',
                borderRadius: '.2rem'
                }}
            >
                {isLoading ? (
                <>
                    <span className="spinner"></span> Redirecting...
                </>
                ) : 'Done'}
            </button>
                <button
                onClick={resetForm}
                style={{
                padding: '.3rem 1rem',
                margin: '0 0 0 1rem',
                border: 'none',
                borderRadius: '.2rem'
                }}
            >
                {isLoading ? (
                <>
                    <span className="spinner"></span> Reseting the form...
                </>
                ) : 'Reset Form'}
            </button>
        </div>
    </div>
  );
};

export default PasswordReset;